import { createAuthClient } from 'better-auth/react';
import { organizationClient, twoFactorClient } from 'better-auth/client/plugins';
import { passkeyClient } from '@better-auth/passkey/client';

/** Same origin as the app; the edge forwards /api/* to the API (ADR-004, ADR-013). */
export const authClient = createAuthClient({
  baseURL: window.location.origin,
  basePath: '/api/auth',
  plugins: [twoFactorClient(), passkeyClient(), organizationClient()],
});

export type Me = { userId: string; role: string; orgId: string; accountIds: string[]; previewOf?: string };
export type Gate = { kind: 'ok'; me: Me } | { kind: 'signin' } | { kind: 'enrol' } | { kind: 'verify' } | { kind: 'recover' } | { kind: 'no-org' };

/**
 * Where should this person be? Signed out → sign-in. Staff without a second factor → security setup.
 * No active org (e.g. after a refused switch, spike F3) → pick their only org automatically.
 */
const LAST_ORG = 'caios.lastOrg';
/** Remember the org in use on this device, so the next sign-in returns to it. */
export function rememberOrg(orgId: string) { try { localStorage.setItem(LAST_ORG, orgId); } catch { /* storage blocked */ } }

export async function gate(): Promise<Gate> {
  const r = await fetch('/api/v1/me', { credentials: 'include' });
  if (r.status === 401) return { kind: 'signin' };
  if (r.ok) { const me = await r.json(); if (me?.orgId) rememberOrg(me.orgId); return { kind: 'ok', me }; }
  const body = await r.json().catch(() => ({}));
  if (body.error === 'mfa_enrollment_required') return { kind: 'enrol' };
  if (body.error === 'second_factor_required') return { kind: 'verify' };
  if (body.error === 'recovery_enrolment') return { kind: 'recover' };
  if (body.error === 'no_active_org') {
    // Pick the org for this session: the one used last on this device, else the first one joined (for the owner,
    // the operator org: ARC). Someone in several orgs (the owner of ARC, Live Echo and 6 Hands) must never be
    // left without one — that was a 403 on every request after the 10-22 import.
    const orgs = (await authClient.organization.list()).data ?? [];
    if (orgs.length) {
      let last: string | null = null;
      try { last = localStorage.getItem(LAST_ORG); } catch { /* storage blocked */ }
      const pick = orgs.find((o) => o.id === last) ?? [...orgs].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())[0]!;
      await authClient.organization.setActive({ organizationId: pick.id });
      return gate();
    }
    return { kind: 'no-org' };
  }
  return { kind: 'signin' };
}
